import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { refundsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { Refund, RefundStatus } from '@/types/finance';
import { formatDate, formatMoney, orDash } from '@/utils/format';
import { Money, RefundStatusBadge } from './shared';
import { ApprovalDialog } from './ApprovalDialog';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Money going back.
 *
 * <p>Three decisions by up to three people, and the screen shows which one a
 * refund is waiting on. A request is not an approval and an approval is not a
 * payment — the buttons only ever offer the next legitimate step, so nobody can
 * hand money back on a refund that has not been authorised.
 */
export function RefundsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('requestedAt,desc');

  const [statusFilter, setStatusFilter] = useState<RefundStatus | ''>('');
  const [deciding, setDeciding] = useState<Refund | null>(null);
  const [processing, setProcessing] = useState<Refund | null>(null);
  const [processForm, setProcessForm] = useState({ refundDate: today(), referenceNumber: '' });
  const [cancelling, setCancelling] = useState<Refund | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const params = {
    ...table.params,
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.refunds.list(params),
    queryFn: () => refundsApi.list(params),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.refunds.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
  };

  const decide = useMutation({
    mutationFn: ({ id, approved, reason }: { id: string; approved: boolean; reason: string }) =>
      refundsApi.decide(id, { approved, ...(reason.trim() !== '' ? { reason } : {}) }),
    onSuccess: (updated) => {
      toast.success(updated.status === 'APPROVED'
        ? 'Approved — the money has not moved yet'
        : 'Refund refused');
      setDeciding(null);
      refresh();
    },
    onError: (error) => { setDeciding(null); handleApiError(error); },
  });

  const process = useMutation({
    mutationFn: (id: string) =>
      refundsApi.process(id, {
        refundDate: processForm.refundDate,
        ...(processForm.referenceNumber !== '' ? { referenceNumber: processForm.referenceNumber } : {}),
      }),
    onSuccess: (updated) => {
      const reopened = updated.reversedAllocations.length;
      toast.success(
        reopened === 0
          ? `${updated.refundNumber} processed from money held on account`
          : `${updated.refundNumber} processed — ${reopened} invoice${reopened === 1 ? '' : 's'} reopened`,
      );
      setProcessing(null);
      refresh();
    },
    onError: (error) => { setProcessing(null); handleApiError(error); },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => refundsApi.cancel(id, cancelReason.trim()),
    onSuccess: () => {
      toast.success('Refund withdrawn');
      setCancelling(null);
      setCancelReason('');
      refresh();
    },
    onError: (error) => { setCancelling(null); handleApiError(error); },
  });

  const columns: Column<Refund>[] = [
    {
      key: 'number',
      header: 'Refund',
      sortKey: 'refundNumber',
      render: (r) => (
        <div>
          <span className="font-mono text-xs font-medium text-ink">{r.refundNumber}</span>
          <span className="block text-[11px] text-subtle">
            against{' '}
            <Link to={ROUTES.paymentDetail(r.paymentId)} className="text-accent hover:underline">
              {r.receiptNumber}
            </Link>
          </span>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (r) => <span className="text-ink">{orDash(r.studentName)}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortKey: 'amount',
      className: 'text-right',
      render: (r) => (
        <div>
          <Money amount={r.amount} bold />
          <span className="block text-right text-[10.5px] text-subtle">
            of {formatMoney(r.paymentAmount, r.currencyCode)}
          </span>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (r) => <span className="text-[13px] text-muted">{r.reason}</span>,
    },
    {
      key: 'when',
      header: 'Requested',
      sortKey: 'requestedAt',
      render: (r) => (
        <div className="whitespace-nowrap text-[13px]">
          <span className="tabular-nums text-ink">{formatDate(r.requestedAt.slice(0, 10))}</span>
          {r.refundDate !== undefined && (
            <span className="block text-[11px] text-muted">paid {formatDate(r.refundDate)}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (r) => <RefundStatusBadge status={r.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'REQUESTED' && can(PERMISSIONS.REFUND_APPROVE) && (
            <Button size="sm" onClick={() => setDeciding(r)}>Decide</Button>
          )}
          {r.status === 'APPROVED' && can(PERMISSIONS.REFUND_PROCESS) && (
            <Button size="sm" onClick={() => {
              setProcessForm({ refundDate: today(), referenceNumber: '' });
              setProcessing(r);
            }}>Pay out</Button>
          )}
          {(r.status === 'REQUESTED' || r.status === 'APPROVED')
            && can(PERMISSIONS.REFUND_REQUEST) && (
            <Button size="sm" variant="ghost" onClick={() => setCancelling(r)}>Withdraw</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Refunds"
        description="Someone asks, someone authorises, someone hands the money back. Nothing moves until the last step."
        icon={<Undo2 className="h-5 w-5" aria-hidden="true" />}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by refund number…',
        }}
        filters={
          <FilterSelect label="Status" value={statusFilter}
                        onChange={(value) => { setStatusFilter(value); table.setPage(0); }}
                        options={[
                          { value: 'REQUESTED', label: 'Awaiting a decision' },
                          { value: 'APPROVED', label: 'Approved, not paid' },
                          { value: 'PROCESSED', label: 'Paid out' },
                          { value: 'REJECTED', label: 'Refused' },
                          { value: 'CANCELLED', label: 'Withdrawn' },
                        ]} />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No refunds"
          emptyDescription="Refunds are requested from a payment, on the payment's own screen."
          emptyIcon={<Undo2 className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <ApprovalDialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        pending={decide.isPending}
        title={deciding === null ? '' : `${deciding.refundNumber} — ${formatMoney(deciding.amount, deciding.currencyCode)}`}
        summary={
          deciding === null
            ? ''
            : `${orDash(deciding.studentName)} · ${deciding.reason}. Approving does not move any money; it authorises someone to.`
        }
        onDecide={(approved, reason) =>
          deciding !== null && decide.mutate({ id: deciding.id, approved, reason })
        }
      />

      <Modal
        open={processing !== null}
        title={processing === null ? '' : `Pay out ${processing.refundNumber}`}
        description={
          processing === null
            ? ''
            : `${formatMoney(processing.amount, processing.currencyCode)} back to ${orDash(processing.studentName)}. Money held on account is released first; only if that is not enough are settled bills reopened.`
        }
        onClose={() => setProcessing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setProcessing(null)}>Not yet</Button>
            <Button loading={process.isPending}
                    onClick={() => processing !== null && process.mutate(processing.id)}>
              Record the payout
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="Date the money left" type="date" required value={processForm.refundDate}
                     onChange={(event) =>
                       setProcessForm((f) => ({ ...f, refundDate: event.target.value }))} />
          <TextField label="Reference" placeholder="Bank transfer reference, cheque number"
                     value={processForm.referenceNumber}
                     onChange={(event) =>
                       setProcessForm((f) => ({ ...f, referenceNumber: event.target.value }))} />
          <p className="text-xs text-muted">
            This is the step that moves money. It cannot be undone — returning it would be a new
            payment, not a change to this record.
          </p>
        </div>
      </Modal>

      <Modal
        open={cancelling !== null}
        title="Withdraw this refund?"
        description={cancelling === null ? '' : `${cancelling.refundNumber} — ${formatMoney(cancelling.amount, cancelling.currencyCode)}`}
        onClose={() => setCancelling(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelling(null)}>Keep it</Button>
            <Button variant="danger" loading={cancel.isPending} disabled={cancelReason.trim() === ''}
                    onClick={() => cancelling !== null && cancel.mutate(cancelling.id)}>
              Withdraw
            </Button>
          </>
        }
      >
        <TextField label="Reason" required placeholder="Family changed their mind"
                   value={cancelReason}
                   onChange={(event) => setCancelReason(event.target.value)} />
      </Modal>
    </>
  );
}
